import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import { Button } from '@/components/ui/Button';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { usePrompt } from '@/components/ui/PromptDialog';
import type { FinAccount, FinBudget, BudgetVarianceRow, FinFinancialPeriod } from '@/types/finance';
import { notify } from '@/core/toast/notify';

export function FinanceSetupPage() {
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { prompt, dialog: promptDialog } = usePrompt();
  const accountsPagination = usePagination(20);
  const [accounts, setAccounts] = useState<FinAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [budgets, setBudgets] = useState<FinBudget[]>([]);
  const [variance, setVariance] = useState<BudgetVarianceRow[]>([]);
  const [periods, setPeriods] = useState<FinFinancialPeriod[]>([]);
  const fiscalYear = new Date().getFullYear();

  const loadAccounts = useCallback(() => {
    setAccountsLoading(true);
    financeApi
      .listAccounts({ page: accountsPagination.page, size: accountsPagination.size })
      .then((r) => {
        setAccounts(r.content);
        accountsPagination.applyPageResponse(r);
      })
      .catch((e) => showApiError(e, 'Failed to load accounts'))
      .finally(() => setAccountsLoading(false));
  }, [accountsPagination.page, accountsPagination.size, accountsPagination]);

  const loadMeta = useCallback(() => {
    financeApi.listBudgets(fiscalYear).then(setBudgets).catch((e) => showApiError(e, 'Failed to load budgets'));
    financeApi.budgetVariance(fiscalYear).then(setVariance).catch(() => setVariance([]));
    financeApi.listFiscalPeriods().then(setPeriods).catch((e) => showApiError(e, 'Failed to load fiscal periods'));
  }, [fiscalYear]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const reloadAll = () => {
    loadAccounts();
    loadMeta();
  };

  async function lockPeriod(id: number) {
    const ok = await confirm({
      title: 'Lock fiscal period',
      message: 'Lock this fiscal period? New journals will be blocked for this range.',
      variant: 'danger',
      confirmLabel: 'Lock',
    });
    if (!ok) return;
    try {
      await financeApi.lockFiscalPeriod(id);
      notify.success('Period locked');
      reloadAll();
    } catch (e) {
      showApiError(e, 'Failed to lock period');
    }
  }

  async function reopenPeriod(id: number) {
    const ok = await confirm({
      title: 'Reopen fiscal period',
      message: 'Reopen this period? Posting will be allowed again for this date range.',
      confirmLabel: 'Reopen',
    });
    if (!ok) return;
    try {
      await financeApi.reopenFiscalPeriod(id);
      notify.success('Period reopened');
      reloadAll();
    } catch (e) {
      showApiError(e, 'Failed to reopen period');
    }
  }

  async function createBudget() {
    const name = await prompt({
      title: 'New budget',
      label: 'Budget name',
      defaultValue: `${fiscalYear} Operating`,
    });
    if (!name) return;
    const code = await prompt({
      title: 'New budget',
      label: 'Budget code',
      defaultValue: `BUD-${fiscalYear}`,
    });
    if (!code) return;
    const accountId = accounts[0]?.id;
    try {
      await financeApi.createBudget({
        budgetName: name,
        budgetCode: code,
        fiscalYear,
        lines: accountId
          ? [{ accountId, plannedAmount: 10000 }]
          : [],
      });
      reloadAll();
    } catch (e) {
      showApiError(e, 'Failed to create budget');
    }
  }

  return (
    <div>
      {confirmDialog}
      {promptDialog}
      <PageHeader
        title="Finance Setup"
        description="Fiscal periods, budgets, variance, and chart of accounts"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Setup' }]}
      />
      <div className="card p-4 mb-4">
        <h3 className="font-semibold mb-3">Fiscal periods</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Code</th>
              <th className="p-2 text-left">Start</th>
              <th className="p-2 text-left">End</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.id} className="border-b">
                <td className="p-2 font-mono">{p.periodCode}</td>
                <td className="p-2">{p.startDate}</td>
                <td className="p-2">{p.endDate}</td>
                <td className="p-2">{p.locked ? 'LOCKED' : p.statusCode}</td>
                <td className="p-2 text-right">
                  {!p.locked ? (
                    <Button variant="secondary" onClick={() => void lockPeriod(p.id)}>
                      Lock
                    </Button>
                  ) : (
                    <Button variant="secondary" onClick={() => void reopenPeriod(p.id)}>
                      Reopen
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {periods.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-slate-500">
                  No periods — finance setup seeds fiscal year on first use
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold">Budgets ({fiscalYear})</h3>
            <Button type="button" size="sm" onClick={() => void createBudget()}>
              New budget
            </Button>
          </div>
          <ul className="text-sm space-y-1">
            {budgets.map((b) => (
              <li key={b.id}>{b.budgetCode} — {b.budgetName} ({b.statusCode})</li>
            ))}
          </ul>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold mb-3">Budget vs actual</h3>
          {variance.length === 0 ? (
            <p className="text-sm text-slate-500">Create a budget with account lines to see variance.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Account</th>
                  <th className="p-2 text-right">Planned</th>
                  <th className="p-2 text-right">Actual</th>
                  <th className="p-2 text-right">Variance</th>
                </tr>
              </thead>
              <tbody>
                {variance.map((v, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2">{v.accountCode ?? v.accountId}</td>
                    <td className="p-2 text-right font-mono">{v.planned?.toFixed(2)}</td>
                    <td className="p-2 text-right font-mono">{v.actual?.toFixed(2)}</td>
                    <td className="p-2 text-right font-mono">{v.variance?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="card p-4 mt-4">
        <h3 className="font-semibold mb-2">Chart of accounts</h3>
        <ul className="text-sm columns-2 gap-4">
          {accounts.map((a) => (
            <li key={a.id}>{a.accountCode} — {a.accountName}</li>
          ))}
        </ul>
        <TablePagination
          page={accountsPagination.page}
          totalPages={accountsPagination.totalPages}
          totalElements={accountsPagination.totalElements}
          onPageChange={accountsPagination.setPage}
          loading={accountsLoading}
          hasRows={accounts.length > 0}
        />
      </div>
    </div>
  );
}
